import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  SwipeableDrawer,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

const SettingsDrawer = ({
  header,
  onSave,
  initialData = {},
  config = {
    showBody: false,
    showImage: false,
    imageSize: { width: 75, height: 75 },
    type: "default",
  },
  edit,
  setEdit,
}) => {
  const defaultFormData = {
    title: "",
    body: "",
    link: "",
    isBold: false,
    isClickable: true,
    image: null,
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [validationError, setValidationError] = useState("");

  // Sync initialData changes with formData
  useEffect(() => {
    setFormData({ ...defaultFormData, ...initialData });
  }, [initialData]);

  // Helper for updating form state
  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Image resizing function
  const resizeImage = (file, width, height) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          canvas.getContext("2d").drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Image upload handler
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const resizedImage = await resizeImage(
          file,
          config.imageSize.width,
          config.imageSize.height
        );
        updateFormField("image", resizedImage);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }
  };

  // Save handler
  const handleSave = () => {
    if (!formData.title.trim()) {
      setValidationError("Title is required");
      return;
    }
    setValidationError("");
    onSave({ ...formData, type: config.type });
    setFormData(defaultFormData); // Reset form after save
  };

  // Render toggle buttons
  const renderToggleButtons = (field, options) =>
    options.map((option) => (
      <Button
        key={option.label}
        variant={formData[field] === option.value ? "contained" : "outlined"}
        size="small"
        onClick={() => updateFormField(field, option.value)}
        sx={{
          textTransform: "none",
          bgcolor: formData[field] === option.value ? "#e0e0e0" : "transparent",
          color: "black",
          "&:hover": {
            bgcolor:
              formData[field] === option.value ? "#d5d5d5" : "transparent",
          },
        }}
      >
        {option.label}
      </Button>
    ));

  return (
    <SwipeableDrawer
      anchor="right"
      open={edit.open}
      onClose={() => setEdit({ open: false, edit: "", title: "" })}
      PaperProps={{
        sx: {
          height: "auto",
          maxHeight: "90vh",
          width: "300px",
          p: 2,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ bgcolor: "white", borderRadius: "4px", width: "100%" }}>
        {/* Header */}
        <Typography
          sx={{
            borderBottom: "1px solid #e0e0e0",
            pb: 1,
            mb: 2,
            fontSize: "14px",
          }}
        >
          {header}
        </Typography>

        {/* Title Section */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 0.5, fontSize: "12px" }}>Title:</Typography>
          <TextField
            fullWidth
            size="small"
            value={formData.title}
            onChange={(e) => updateFormField("title", e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            {renderToggleButtons("isBold", [
              { label: "Regular", value: false },
              { label: "Bold", value: true },
            ])}
          </Box>
        </Box>

        {/* Body Section */}
        {config.showBody && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 0.5, fontSize: "12px" }}>Body:</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              value={formData.body}
              onChange={(e) => updateFormField("body", e.target.value)}
              sx={{ mb: 1 }}
            />
          </Box>
        )}

        {/* Link Section */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ mb: 0.5, fontSize: "12px" }}>Link:</Typography>
          <TextField
            fullWidth
            size="small"
            value={formData.link}
            onChange={(e) => updateFormField("link", e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            {renderToggleButtons("isClickable", [
              { label: "Clickable", value: true },
              { label: "Not Clickable", value: false },
            ])}
          </Box>
        </Box>

        {/* Image Section */}
        {config.showImage && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 0.5, fontSize: "12px" }}>Image:</Typography>
            {formData.image ? (
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <img
                  src={formData.image}
                  alt="uploaded"
                  style={{
                    width: config.imageSize.width,
                    height: config.imageSize.height,
                    objectFit: "cover",
                    marginRight: 8,
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => updateFormField("image", null)}
                  sx={{ color: "red" }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<AddPhotoAlternateIcon />}
              >
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </Button>
            )}
          </Box>
        )}

        {/* Save Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleSave}
          sx={{
            bgcolor: "#2e7d32",
            color: "white",
            "&:hover": { bgcolor: "#1b5e20" },
          }}
        >
          Save
        </Button>

        {validationError && (
          <Typography color="error" sx={{ mt: 1, fontSize: "12px" }}>
            {validationError}
          </Typography>
        )}
      </Box>
    </SwipeableDrawer>
  );
};

export default SettingsDrawer;
